/**
 * Fetch-based SSE client. Unlike EventSource it lets us:
 *  - resume explicitly after a page reload (`?after=<seq>` + `Last-Event-ID` header),
 *  - control reconnect/backoff and stop deterministically on terminal events,
 *  - run against the in-memory mock (which returns a ReadableStream) and in tests.
 */
import { orgHeaders } from './active-org';

export interface SSEMessage {
  id: string | null;
  event: string;
  data: string;
}

/** Incremental parser for the `text/event-stream` format (WHATWG HTML §9.2). */
export function createSSEParser(onMessage: (msg: SSEMessage) => void) {
  let buffer = '';
  let data: string[] = [];
  let event = '';
  let id: string | null = null;
  let lastId: string | null = null;

  const dispatch = () => {
    if (data.length === 0) {
      event = '';
      return;
    }
    if (id !== null) lastId = id;
    onMessage({ id: lastId, event: event || 'message', data: data.join('\n') });
    data = [];
    event = '';
    id = null;
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      let idx = buffer.search(/\r\n|\r|\n/);
      while (idx !== -1) {
        const nlLen = buffer[idx] === '\r' && buffer[idx + 1] === '\n' ? 2 : 1;
        const line = buffer.slice(0, idx);
        buffer = buffer.slice(idx + nlLen);
        if (line === '') dispatch();
        else if (!line.startsWith(':')) {
          const colon = line.indexOf(':');
          const field = colon === -1 ? line : line.slice(0, colon);
          let value = colon === -1 ? '' : line.slice(colon + 1);
          if (value.startsWith(' ')) value = value.slice(1);
          if (field === 'data') data.push(value);
          else if (field === 'event') event = value;
          else if (field === 'id' && !value.includes('\0')) id = value;
        }
        idx = buffer.search(/\r\n|\r|\n/);
      }
    },
    get lastEventId() {
      return lastId;
    },
  };
}

export type SSEState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SSEOptions {
  url: string;
  /** Resume point. Sent as `Last-Event-ID` header and `?after=` query param. */
  lastEventId?: string | null;
  onMessage: (msg: SSEMessage) => void;
  onStateChange?: (s: SSEState) => void;
  /** Return true to stop (no reconnect) after this message, e.g. on a terminal run status. */
  isTerminal?: (msg: SSEMessage) => boolean;
  fetchImpl?: typeof fetch;
  /** Backoff in ms for attempt n (0-based). */
  backoff?: (attempt: number) => number;
  maxAttempts?: number;
}

export const defaultBackoff = (attempt: number) => Math.min(10_000, 500 * 2 ** attempt) + Math.random() * 250;

export function withAfter(url: string, after: string | null | undefined): string {
  if (after == null || after === '') return url;
  const [path, qs = ''] = url.split('?');
  const params = new URLSearchParams(qs);
  params.set('after', after);
  return `${path}?${params.toString()}`;
}

export interface SSEConnection {
  close(): void;
  readonly lastEventId: string | null;
  /** Resolves when the connection is permanently closed. */
  done: Promise<void>;
}

export function connectSSE(opts: SSEOptions): SSEConnection {
  const fetchImpl = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const backoff = opts.backoff ?? defaultBackoff;
  const maxAttempts = opts.maxAttempts ?? Number.POSITIVE_INFINITY;
  let lastEventId = opts.lastEventId ?? null;
  let closed = false;
  let controller: AbortController | null = null;
  let wake: (() => void) | null = null;

  const setState = (s: SSEState) => opts.onStateChange?.(s);

  const run = async () => {
    let attempt = 0;
    while (!closed) {
      controller = new AbortController();
      setState(attempt === 0 ? 'connecting' : 'reconnecting');
      let terminal = false;
      try {
        const headers: Record<string, string> = {
          accept: 'text/event-stream',
          'cache-control': 'no-cache',
          ...orgHeaders(),
        };
        if (lastEventId) headers['last-event-id'] = lastEventId;
        const res = await fetchImpl(withAfter(opts.url, lastEventId), {
          headers,
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (res.status === 401 || res.status === 403 || res.status === 404) {
          // Not retryable.
          break;
        }
        if (!res.ok || !res.body) throw new Error(`SSE HTTP ${res.status}`);
        setState('open');
        attempt = 0;
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        const parser = createSSEParser((msg) => {
          if (msg.id != null) lastEventId = msg.id;
          opts.onMessage(msg);
          if (opts.isTerminal?.(msg)) terminal = true;
        });
        while (!closed && !terminal) {
          const { value, done } = await reader.read();
          if (done) break;
          parser.push(value);
        }
        if (terminal) {
          reader.cancel().catch(() => {});
          break;
        }
      } catch (err) {
        if (closed) break;
        if ((err as Error).name !== 'AbortError') {
          // fall through to backoff
        }
      }
      if (closed) break;
      if (attempt >= maxAttempts) break;
      const delay = backoff(attempt++);
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, delay);
        wake = () => {
          clearTimeout(t);
          resolve();
        };
      });
      wake = null;
    }
    closed = true;
    setState('closed');
  };

  const done = run();

  return {
    close() {
      closed = true;
      controller?.abort();
      wake?.();
    },
    get lastEventId() {
      return lastEventId;
    },
    done,
  };
}
